import { Card, CardContent, CardHeader, CardTitle, ClipboardList } from '@chain-zen/ui'

import { LabelRow } from '../../../../../components/shared/label-row'
import { useAuth } from '../../../../../hooks/use-auth'
import type { IShipment } from '../../../../../types/shipment'
import { filterAsChaineUser, isChainWorkspace } from '../../../../../utils/chaine-workspace-utils'
import { PriorityMenu } from './priority-menu'
import { ReleaseInput } from './release-input'
import { ShipmentDispatchers } from './shipment-dispatchers'
import { ShipmentDrivers } from './shipment-drivers'
import { ShipmentMembers } from './shipment-members'
import { ShipmentStatusMenu } from './shipment-status-menu'
import { ShipmentTeamMembers } from './shipment-team-members'
import { ShipmentTrailerTruck } from './shipment-trailer-truck'

/**
 * This component is used to display the details of a shipment.
 * It displays the details of the shipment and allows the user to interact with the shipment.
 * It displays the status, team members, dispatchers, and drivers of the shipment.
 */
export const Details = ({ shipment }: { shipment: IShipment }) => {
  const { selectedWorkspace } = useAuth()

  if (!shipment) return null

  const workspaceID = shipment?.workspace?.id ?? selectedWorkspace?.workspace.id ?? ''

  const isAChaineWorkspace = isChainWorkspace(selectedWorkspace?.workspace.id || null)

  /* Remove chaine users from the list **/
  const filteredTeamMembers = shipment?.teamMembers.filter(
    (t) => !filterAsChaineUser({ email: t.email, selectedWorkspaceID: selectedWorkspace?.workspace.id || null })
  )

  /* Remove chaine users from the list. Guard t?.user since dispatcher.workspace can be null from partial API response. */
  const filteredDispatchers = shipment?.dispatchers?.filter(
    (t) =>
      t?.user &&
      !filterAsChaineUser({
        email: t.user.email,
        selectedWorkspaceID: selectedWorkspace?.workspace.id || null
      })
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          Users
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <LabelRow label="Status">
            <ShipmentStatusMenu
              isShipmentOwner={shipment?.isShipmentOwner}
              currentStatus={shipment?.shipmentStatus}
              shipmentID={shipment?.id}
            />
          </LabelRow>

          {!!isAChaineWorkspace && (
            <>
              <LabelRow label="Release">
                <ReleaseInput release={shipment?.release || null} shipmentID={shipment?.id} />
              </LabelRow>

              <LabelRow label="Priority">
                <PriorityMenu currentPriority={shipment.priority || null} shipmentID={shipment.id} />
              </LabelRow>
            </>
          )}

          {shipment.isShipmentOwner ? (
            <LabelRow
              label="Team Members"
              tooltipText="Team members are users that are a part of your company or team who are assigned to the shipment."
            >
              <ShipmentTeamMembers
                selectedUsers={filteredTeamMembers}
                shipmentID={shipment.id}
                workspaceID={workspaceID}
              />
            </LabelRow>
          ) : (
            <LabelRow label="Brokers" tooltipText="The broker who assigned you to the shipment.">
              <ShipmentMembers members={shipment.teamMembers} shipmentWorkspace={shipment.shipmentOwnerWorkspace} />
            </LabelRow>
          )}

          <LabelRow label="Dispatchers" tooltipText="Add dispatchers to the shipment">
            <ShipmentDispatchers
              shipmentID={shipment.id}
              workspaceID={workspaceID}
              dispatchers={filteredDispatchers || []}
              shipmentNumber={shipment.shipmentNumber}
              shipmentStatus={shipment.shipmentStatus}
            />
          </LabelRow>

          <LabelRow label="Drivers" tooltipText="Add drivers to the shipment">
            <ShipmentDrivers
              shipmentID={shipment.id}
              workspaceID={workspaceID}
              selectedUsers={shipment.drivers}
              shipmentNumber={shipment.shipmentNumber}
              shipmentStatus={shipment.shipmentStatus}
            />
          </LabelRow>

          <LabelRow label="Trailer / Truck #" tooltipText="Add trailer and truck numbers to the shipment">
            <ShipmentTrailerTruck
              dispatchers={shipment?.dispatchers}
              shipmentID={shipment?.id}
              shipmentOwnerWorkspaceID={shipment?.shipmentOwnerWorkspace?.id || workspaceID}
              trailerNumber={shipment?.trailerNumber}
              truckNumber={shipment?.truckNumber}
            />
          </LabelRow>
        </div>
      </CardContent>
    </Card>
  )
}
